// src/components/Pages/StrategicTitlePage.js
import React, { useState } from 'react';
import {
    Container,
    TextInput,
    Button,
    Paper,
    Title,
    Text,
    Loader,
    Alert,
    Stack,
    Group,
    Badge,
    Anchor,
    SimpleGrid,
    Box,
    useMantineTheme,
    useMantineColorScheme,
    Tooltip,
    NumberInput
} from '@mantine/core';
import { IconAlertCircle, IconSparkles, IconExternalLink, IconRocket, IconFileText } from '@tabler/icons-react';
import { useAuth } from '../../context/AuthContext';
import { useBackgroundImage } from '../../context/BackgroundImageContext';
import backgroundImageFile from '../../assets/images/brochure-background.jpg';
//import { useNavigate } from 'react-router-dom';

// Helper function for quartile labels (remains the same)
// const deriveQuartileScoreLabel = (score) => {
// if (score === null || score === undefined || isNaN(parseFloat(score))) return "N/A";
// const numericScore = parseFloat(score);
// if (numericScore >= 7.5) return 'Very High';
// if (numericScore >= 5.0) return 'High';
// if (numericScore >= 2.5) return 'Moderate';
// if (numericScore >= 0) return 'Low';
// return "N/A";
// };

// NEW: Helper function for similarity badge colors
// Based on FICSPage's getSimilarityRingColor logic, adapted for badges
// For Internal Overlap: Higher percentage is "more overlap" (hotter colors).
// For Reference Competitor Sim: Higher percentage means "closer match" (hotter colors).
const getSimilarityBadgeColor = (percentage) => {
    if (percentage === null || percentage === undefined) return 'gray';
    if (percentage > 70) return 'red';
    if (percentage > 50) return 'orange';
    if (percentage > 30) return 'yellow';
    return 'blue.4';
};


const STRATEGIC_TITLES_API_URL = 'https://generate-strategic-titles-mvp-u6n4kc7zyq-ew.a.run.app';

function StrategicTitlePage() {
    //const navigate = useNavigate(); // <<< --- 2. INITIALIZE useNavigate HERE
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { isBackgroundImageVisible } = useBackgroundImage();
    const { currentUser } = useAuth();

    const [topic, setTopic] = useState('');
    const [numTitles, setNumTitles] = useState(20);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [results, setResults] = useState(null);
    const [debugTimings, setDebugTimings] = useState(null);

    const sectionContainerBg = isDark ? theme.colors.dark[6] : theme.colors.gray[0];
    const textOnBackgroundImageColor = isBackgroundImageVisible ? theme.white : (isDark ? theme.white : theme.black);
    const mainPageSolidBackgroundColor = isDark ? theme.colors.dark[7] : theme.colors.gray[0];

    const inputStyles = {
        input: {
            backgroundColor: isDark ? theme.colors.dark[5] : theme.white,
            color: isDark ? theme.white : theme.black,
            borderColor: isDark ? theme.colors.dark[4] : theme.colors.gray[4],
            '&::placeholder': {
                color: isDark ? theme.colors.dark[3] : theme.colors.gray[5],
            },
            '&:focus, &:focus-within': {
                borderColor: theme.primaryColor[isDark ? 7 : 5],
            },
        },
    };

    const handleSubmit = async () => {
        // ... (handleSubmit function remains unchanged) ...
        if (!topic.trim()) {
            setError("Please enter a topic or keywords.");
            return;
        }
        setIsLoading(true);
        setError(null);
        setResults(null);
        setDebugTimings(null);

        if (!currentUser) {
            setError("User not authenticated. Please log in.");
            setIsLoading(false);
            return;
        }

        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(STRATEGIC_TITLES_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    topic: topic,
                    num_titles: Number(numTitles)
                }),
            });

            const responseData = await response.json();

            if (!response.ok) {
                throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
            }

            if (responseData.status === "Success") {
                setResults(responseData.generated_analyzed_titles);
                setDebugTimings(responseData.debug_timings);
            } else {
                throw new Error(responseData.message || "Failed to process request.");
            }

        } catch (err) {
            setError(err.message);
            console.error("Strategic Titles API Error:", err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box
            style={{
                width: '100%',
                minHeight: 'calc(100vh - var(--app-shell-header-height, 0px))',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundImage: isBackgroundImageVisible ? `url(${backgroundImageFile})` : 'none',
                backgroundColor: isBackgroundImageVisible ? 'transparent' : mainPageSolidBackgroundColor,
                ...(isBackgroundImageVisible && {
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundAttachment: 'fixed',
                }),
                overflow: 'auto',
            }}
        >
            <Container fluid size="lg" p="md" py="xl" style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Stack gap="md" style={{ flexGrow: 1 }}>
                    <Title order={1} ta="center" style={{ color: textOnBackgroundImageColor }}>
                        <Group justify="center" gap="xs">
                            {/* Keep the IconSparkles color logic as is, or simplify it too if desired */}
                            <IconSparkles size={30} stroke={1.5} color={theme.colors.yellow[isDark || isBackgroundImageVisible ? 4 : 6]} />
                            Title Discovery
                        </Group>
                    </Title>
                    <Text ta="center" style={{ color: textOnBackgroundImageColor }}>
                        Use Title Discovery to generate a set of content suggestions. Select one to proceed to Idea Checker.
                    </Text>
                    <Paper shadow="sm" p="xl" mt="lg" radius="md" withBorder bg={sectionContainerBg} maw={theme.breakpoints.md} mx="auto">

                        <Title order={2} >| Your Theme</Title>
                        <Title order={2} mt="lg"></Title>
                        <Text>
                            Enter a topic, keywords or an idea outline to generate titles and assess their potential.
                        </Text>
                        <Stack gap="lg" >
                            <TextInput
                                label="Topic, Keywords or Idea:"
                                mt="lg"
                                placeholder="E.g. Sustainable Travel, AI in Healthcare, Quick Weeknight Dinners..."
                                value={topic}
                                onChange={(event) => setTopic(event.currentTarget.value)}
                                size="lg"
                                required
                                styles={inputStyles}
                            />
                            <NumberInput
                                label="Number of Titles to Generate:"
                                placeholder="e.g., 20"
                                value={numTitles}
                                onChange={setNumTitles}
                                min={5}
                                max={50}
                                step={5}
                                size="lg"
                                required
                                styles={inputStyles}
                            />
                            <Button
                                onClick={handleSubmit}
                                loading={isLoading}
                                //disabled={!topic.trim()}
                                fullWidth
                                mt="md"
                                size="lg"
                                variant="gradient"
                                gradient={{ from: 'teal', to: 'lime', deg: 105 }}
                            >
                                Generate Titles & Analysis
                            </Button>
                        </Stack>
                    </Paper>

                    {isLoading && (
                        <Group justify="center" mt="xl">
                            <Loader />
                            <Text ml="sm" style={{ color: textOnBackgroundImageColor }}>
                                Generating ideas / analysing archive / retrieving closest competitors... (NB: Cold starts can take a minute. Once warm, it's quicker!)
                            </Text>
                        </Group>
                    )}
                    {error && (
                        <Alert
                            icon={<IconAlertCircle size="1rem" />}
                            title="Error"
                            color="red"
                            mt="xl"
                            withCloseButton
                            onClose={() => setError(null)}
                            variant="filled"
                        >
                            {error}
                        </Alert>
                    )}

                    {results && results.length > 0 && (
                        <Paper shadow="xs" p="lg" mt="xl" withBorder radius="md" bg={sectionContainerBg}>
                            <Title order={3} mb="lg" style={{ color: isDark ? theme.white : theme.black }}>
                                Generated Titles & Analysis ({results.length})
                            </Title>
                            <Paper withBorder p="lg" radius="md" mt="xl" bg="gray.4">
                                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
                                    {results.map((item, index) => {
                                        const internalOverlapPercent = item.internal_similarity_score !== null ? parseFloat((item.internal_similarity_score * 100).toFixed(0)) : null;
                                        const competitorSimilarityPercent = item.reference_competitor?.similarity_to_generated_title !== null && item.reference_competitor?.similarity_to_generated_title !== undefined
                                            ? parseFloat((item.reference_competitor.similarity_to_generated_title * 100).toFixed(0))
                                            : null;
                                        const ficsLink = `/fics?initialTitle=${encodeURIComponent(item.generated_title)}&initialVertical=${encodeURIComponent("General")}`;
                                        const briefBuilderParams = new URLSearchParams();

                                        briefBuilderParams.set('initialTitle', item.generated_title);
                                        if (item.reference_competitor && item.reference_competitor.url) {
                                            briefBuilderParams.set('initialUrls', item.reference_competitor.url);
                                        }
                                        const briefBuilderLink = `/brief-builder?${briefBuilderParams.toString()}`;


                                        return (

                                            <Paper
                                                key={index}
                                                withBorder
                                                p="md"
                                                radius="sm"
                                                shadow="xs"
                                                style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}
                                                bg={isDark ? theme.colors.dark[6] : "gray.1"}
                                            >
                                                <Stack gap="xs">
                                                    <Title order={3} style={{ color: isDark ? theme.colors.gray[1] : theme.black }}>{item.generated_title}</Title>




                                                    <Group gap="xs" mt="xs" >
                                                        <text>Prominence™:</text>
                                                        <Tooltip label="Average Prominence from top 5 competitors">
                                                            <Badge
                                                                size="lg"
                                                                color="grape" // MODIFIED: Fixed color
                                                                variant={isDark ? "outline" : "light"}
                                                            >
                                                                {item.average_prominence_label}
                                                            </Badge>
                                                        </Tooltip><text>Stickiness™:</text>
                                                        <Tooltip label="Average Stickiness from top 5 competitors">
                                                            <Badge
                                                                size="lg"
                                                                color="teal" // MODIFIED: Fixed color
                                                                variant={isDark ? "outline" : "light"}
                                                            >
                                                                {item.average_stickiness_label}
                                                            </Badge>
                                                        </Tooltip>
                                                    </Group>
                                                    <Group gap="xs" mt="xs">
                                                        <text>Archive Overlap:</text>
                                                        <Tooltip label="Similarity to existing internal content. Higher means less unique.">
                                                            <Badge
                                                                color={getSimilarityBadgeColor(internalOverlapPercent)}
                                                                variant={isDark ? "outline" : "filled"}
                                                                size="lg"
                                                                c={isDark ? getSimilarityBadgeColor(internalOverlapPercent) : "gray.9"}
                                                            >
                                                                {internalOverlapPercent !== null ? `${internalOverlapPercent}%` : 'N/A'}
                                                            </Badge>
                                                        </Tooltip>

                                                        <text>Competitor Overlap:</text>
                                                        {/* Reference Competitor Similarity as Badge */}

                                                        {competitorSimilarityPercent !== null && (
                                                            <Tooltip label="Similarity to this generated title">
                                                                <Badge

                                                                    color={getSimilarityBadgeColor(competitorSimilarityPercent)}
                                                                    variant={isDark ? "outline" : "filled"}
                                                                    size="lg"
                                                                    c={isDark ? getSimilarityBadgeColor(competitorSimilarityPercent) : "gray.9"}
                                                                >
                                                                    {`${competitorSimilarityPercent}%`}
                                                                </Badge>
                                                            </Tooltip>

                                                        )}</Group>

                                                    {item.reference_competitor && (
                                                        <Box mt="sm">

                                                            {item.reference_competitor.url ? (
                                                                <Anchor
                                                                    href={item.reference_competitor.url}
                                                                    target="_blank"
                                                                    size="sm"
                                                                    c={isDark ? theme.colors.blue[4] : theme.colors.blue[7]}
                                                                    style={{ display: 'block' }} // <<< ADDED THIS STYLE
                                                                >
                                                                    {item.reference_competitor.title} ({item.reference_competitor.publisher || 'N/A'})
                                                                    <IconExternalLink size={14} style={{ marginLeft: '4px', verticalAlign: 'middle' }} />
                                                                </Anchor>
                                                            ) : (
                                                                <Text
                                                                    size="xs"
                                                                    style={{
                                                                        color: isDark ? theme.colors.gray[3] : theme.colors.dark[2],
                                                                        display: 'block' // <<< ADDED THIS STYLE
                                                                    }}
                                                                >
                                                                    {item.reference_competitor.title} ({item.reference_competitor.publisher || 'N/A'})
                                                                </Text>
                                                            )}

                                                        </Box>
                                                    )}

                                                </Stack><Group gap="xs" mt="xs" justify="flex-end">
                                                    {/*
                                                    <Button
                                                        component="a"
                                                        href={briefBuilderLink}
                                                        target="_blank"           // <<< ADD THIS LINE
                                                        rel="noopener noreferrer" // <<< AND THIS LINE
                                                        mt="xs"
                                                        size="xs"
                                                        color="green"
                                                        variant="filled"
                                                        leftSection={<IconFileText size={16} />}
                                                    > Generate Brief
                                                    </Button>
                                                    */}
                                                    <Button
                                                        component="a" // Render the button as an anchor tag
                                                        href={ficsLink} // The URL to navigate to
                                                        target="_blank" // This makes it open in a new tab
                                                        rel="noopener noreferrer" // Security best practice for target="_blank"
                                                        mt="xs" // Or your existing margin
                                                        size="xs" // Or your existing size
                                                        //variant="filled" // Or your existing variant
                                                        variant = "gradient"
                                                        gradient={{ from: 'teal', to: 'lime', deg: 105 }}
                                                        //color={isDark ? "green.5" : "green.5"}
                                                        leftSection={<IconRocket size={16} />} // Or your existing icon
                                                    >
                                                        Select
                                                    </Button>
                                                </Group>
                                            </Paper>

                                        );
                                    })}
                                </SimpleGrid>
                            </Paper>
                            {debugTimings && (
                                <Box mt="xl" p="md" style={{ backgroundColor: isDark ? theme.colors.dark[8] : theme.colors.gray[1], borderRadius: theme.radius.sm }}>
                                    <Text size="sm" fw={500} style={{ color: isDark ? theme.colors.gray[5] : theme.colors.dark[1] }}>Debug Timings:</Text>
                                    <pre style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', color: isDark ? theme.colors.gray[5] : theme.colors.dark[1] }}>{JSON.stringify(debugTimings, null, 2)}</pre>
                                </Box>
                            )}
                        </Paper>
                    )}
                    {results && results.length === 0 && !isLoading && (
                        <Paper p="xl" mt="xl" shadow="xs" withBorder radius="md" bg={sectionContainerBg}>
                            <Text ta="center" style={{ color: isDark ? theme.colors.gray[5] : theme.colors.gray[7] }}>
                                No titles were generated or analyzed. The LLM might have returned an empty list or encountered an issue.
                            </Text>
                        </Paper>
                    )}
                </Stack>
            </Container>
        </Box>
    );
}

export default StrategicTitlePage;